<template>
  <v-row :class="{ 'selector--transparent': appearance === 'transparent' }" dense align="center">
    <!-- Household Column -->
    <v-col :cols="stacked ? 12 : (showBothSelectors ? 6 : 12)">
      <!-- No Household - Show setup prompt -->
      <HouseholdSetupDialog v-if="!hasHousehold">
        <template #activator="{ props }">
          <v-chip v-bind="props" color="warning" variant="flat" size="small">
            <v-icon start size="16">mdi-home-alert</v-icon>
            No Household
            <v-icon end size="14">mdi-plus-circle</v-icon>
          </v-chip>
        </template>
      </HouseholdSetupDialog>

      <!-- Has multiple households - show household selector -->
      <v-select v-else-if="households.length > 1" v-model="selectedHouseholdId" :items="householdItems" label="Household"
        v-bind="$attrs" hide-details item-title="text" item-value="value">
        <template #prepend-inner>
          <v-icon size="18" color="primary">mdi-home-group</v-icon>
        </template>
      </v-select>

      <!-- Single household - just show chip -->
      <v-chip v-else-if="currentHousehold" color="primary" variant="tonal" size="small">
        <v-icon start size="16">mdi-home-group</v-icon>
        Household: {{ currentHousehold.name || 'My Household' }}
      </v-chip>
    </v-col>

    <!-- Pantry Column (only if showing pantry selector) -->
    <v-col v-if="showPantrySelector" :cols="stacked ? 12 : (showBothSelectors ? 6 : 12)">
      <!-- Pantry selector (show if >1 pantry in household OR if there are orphaned pantries) -->
      <v-select v-if="pantriesInHousehold.length > 1 || hasOrphanedPantries" v-model="selectedPantry"
        :items="pantrySelectItems" label="Pantry" v-bind="$attrs" hide-details item-title="text" item-value="value">
        <template #prepend-inner>
          <v-icon size="18" color="secondary">mdi-fridge-outline</v-icon>
        </template>
      </v-select>

      <!-- Single pantry - just show chip -->
      <v-chip v-else-if="pantriesInHousehold.length === 1" color="secondary" variant="tonal" size="small">
        <v-icon start size="16">mdi-fridge-outline</v-icon>
        Pantry: {{ pantriesInHousehold[0].text }}
      </v-chip>
    </v-col>

    <!-- Orphaned pantries indicator (only show when we don't already show the pantry selector) -->
    <v-col v-if="hasOrphanedPantries && pantriesInHousehold.length <= 1 && !showPantrySelector" cols="auto">
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-chip v-bind="props" color="warning" variant="tonal" size="small" @click="goToPantries">
            <v-icon start size="16">mdi-alert-circle-outline</v-icon>
            {{ orphanedPantryCount }} orphaned
          </v-chip>
        </template>
        <span>{{ orphanedPantryCount }} pantry(ies) not in any household. Click to manage.</span>
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { usePantryStore } from '@/stores/pantryStore'
import { useHouseholdStore } from '@/stores/householdStore'
import HouseholdSetupDialog from './HouseholdSetupDialog.vue'

const router = useRouter()
const userStore = useUserStore()
const pantryStore = usePantryStore()
const householdStore = useHouseholdStore()

// Props
const props = defineProps<{
  /** When true, the selectors are stacked vertically (12 cols each) */
  stacked?: boolean
  /** Visual appearance preset for the selector */
  appearance?: 'default' | 'transparent'
}>()

const stacked = props.stacked ?? false
const appearance = props.appearance ?? 'default'

// Household info
const hasHousehold = computed(() => householdStore.hasHouseholds)
const households = computed(() => householdStore.households)
const currentHousehold = computed(() => householdStore.currentHousehold)

// Household selector items
const householdItems = computed(() => {
  return households.value.map(h => ({
    text: h.name || 'Unnamed Household',
    value: h.id
  }))
})

// Selected household
const selectedHouseholdId = computed({
  get: () => householdStore.currentHousehold?.id ?? null,
  set: (value: string | null) => {
    if (value) {
      householdStore.setCurrentHousehold(value)
    }
  }
})

// Pantries in current household
const pantriesInHousehold = computed(() => {
  const pantryIds = currentHousehold.value?.pantryIds ?? []
  return pantryIds
    .map((pantryId: string) => {
      const pantry = pantryStore.pantry(pantryId)
      if (!pantry?.name) return null
      return { text: pantry.name, value: pantryId }
    })
    .filter((item): item is { text: string; value: string } => item !== null)
})

// Items to show in the pantry selector. If there are no pantries in the household
// but orphaned pantries exist, show a placeholder option that navigates to the pantries page.
const pantrySelectItems = computed(() => {
  if (pantriesInHousehold.value.length > 0) return pantriesInHousehold.value
  if (hasOrphanedPantries.value) return [{ text: 'Manage pantries', value: '__manage__' }]
  return []
})

// Selected pantry
const selectedPantry = computed({
  get: () => userStore.currentPantry,
  set: (value: string) => {
    if (value === '__manage__') {
      goToPantries()
      return
    }
    userStore.setCurrentPantry(value)
  }
})

// Orphaned pantries (not in any household)
const orphanedPantryIds = computed(() => {
  const allPantryIds = pantryStore.getPantriesDocs
  return allPantryIds.filter(id => householdStore.isPantryOrphaned(id))
})

const hasOrphanedPantries = computed(() => orphanedPantryIds.value.length > 0)
const orphanedPantryCount = computed(() => orphanedPantryIds.value.length)

// Show pantry selector if multiple pantries or orphaned pantries exist
const showPantrySelector = computed(() => {
  return pantriesInHousehold.value.length > 1 || hasOrphanedPantries.value || pantriesInHousehold.value.length === 1
})

// Show both selectors side by side (cols=6 each) when both household and pantry selectors are visible
const showBothSelectors = computed(() => {
  const hasHouseholdSelector = households.value.length > 1
  const hasPantrySelector = pantriesInHousehold.value.length > 1 || hasOrphanedPantries.value
  return hasHouseholdSelector && hasPantrySelector
})

// Navigate to pantries page
const goToPantries = () => {
  router.push('/pantry')
}

// Auto-select first pantry in household if current selection is not in the household
watch(
  [pantriesInHousehold, selectedPantry],
  ([pantries, current]) => {
    if (pantries.length > 0) {
      const isCurrentInHousehold = pantries.some(p => p.value === current)
      if (!isCurrentInHousehold) {
        userStore.setCurrentPantry(pantries[0].value)
      }
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.selector--transparent :deep(.v-field) {
  background: rgba(255, 255, 255, 0.15);
}

.selector--transparent :deep(.v-field__input),
.selector--transparent :deep(.v-select__selection-text),
.selector--transparent :deep(.v-field__append-inner .v-icon),
.selector--transparent :deep(.v-label) {
  color: white !important;
}
</style>
